import { useState } from 'react'
import { FORMS_API, useTurnstile } from '../lib/turnstile'

type Props = {
  /** Where the signup came from, recorded with it and sent to beehiiv as utm_campaign. */
  source: string
  /** `article` renders the boxed end-of-article version with its own heading. */
  variant?: 'footer' | 'article'
}

// Email signup for the newsletter. Separate from the Starter Kit on purpose:
// the kit stays a no-email instant download (TK, 2026-10-14). Signups go to
// WP first (kept even if beehiiv is down), then on to beehiiv, which sends the
// welcome email.
export default function DispatchSignup({ source, variant = 'footer' }: Props) {
  const [done, setDone] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const ts = useTurnstile()

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const fd = new FormData(e.currentTarget)
    setBusy(true)
    setError('')
    try {
      const turnstile = await ts.getToken()
      const res = await fetch(`${FORMS_API}/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: fd.get('email'), company: fd.get('company'), source, turnstile }),
      })
      const json = await res.json().catch(() => ({}))
      if (res.ok && json.ok) setDone(true)
      else setError(json.error || 'Something went wrong. Please try again.')
    } catch {
      setError('Network error. Please try again.')
    } finally {
      ts.reset()
      setBusy(false)
    }
  }

  return (
    <div className={`sgn sgn-${variant}`}>
      {variant === 'article' ? (
        <>
          <div className="label">The Dispatch</div>
          <h2>Get the next one by email</h2>
          <p className="sgn-sub">New field-tested guides in your inbox as they publish. Free, no spam, one-click unsubscribe.</p>
        </>
      ) : (
        <p className="sgn-sub">New guides by email as they publish. No spam, unsubscribe anytime.</p>
      )}
      {done ? (
        <p className="sgn-ok" role="status">
          You're in. Watch your inbox for the next dispatch.
        </p>
      ) : (
        <form className="sgn-row" onSubmit={onSubmit} aria-label={`Newsletter signup, ${variant}`}>
          <input type="text" name="company" tabIndex={-1} autoComplete="off" aria-hidden="true" className="sgn-hp" />
          <input
            type="email"
            name="email"
            required
            autoComplete="email"
            placeholder="you@email.com"
            aria-label="Email address"
            onFocus={() => void ts.prime()}
          />
          <button className="btn sig" type="submit" disabled={busy}>
            {busy ? 'Joining…' : 'Subscribe'}
          </button>
        </form>
      )}
      {error && (
        <p className="sgn-err" role="alert">
          {error}
        </p>
      )}
      <div ref={ts.ref} className="sgn-ts" />
    </div>
  )
}
